/**
 * Shopify + Odoo bridge for the liner Fit Quiz.
 * Source copied to shopify-zipfit-theme as assets/fit-quiz-bridge.js.
 * Reads window.ZIPFIT_FIT_QUIZ_CONFIG from the theme section.
 */
(function (global) {
  const cfg = () => global.ZIPFIT_FIT_QUIZ_CONFIG || {};
  const DRAFT_KEY = 'zipfit_fit_quiz_draft_v1';
  const PENDING_EVENTS_KEY = 'zipfit_fit_quiz_events_pending_v1';
  const MONDO_OPTION_NAME = 'Size (Mondo Point)';
  let generation = 1;
  let generationToken = null;
  const inflightEventIds = new Set();

  function applyThemeCatalogOverrides() {
    const c = cfg();
    if (c.linerImages && global.LINER_IMG) {
      Object.keys(c.linerImages).forEach((key) => {
        const url = c.linerImages[key];
        if (url) global.LINER_IMG[key] = url;
      });
    }
    if (c.linerShopUrls && global.LINER_SHOP_URL) {
      Object.keys(c.linerShopUrls).forEach((key) => {
        const url = c.linerShopUrls[key];
        if (url) global.LINER_SHOP_URL[key] = url;
      });
    }
  }

  function mondoOptionName() {
    return (cfg().mondoOptionName || MONDO_OPTION_NAME).trim();
  }

  function apiBase() {
    let base = (cfg().apiBase || '').trim().replace(/\/$/, '');
    // Theme setting should be FastAPI root only (…/api/v1), not the submissions path.
    base = base.replace(/\/fit-quiz\/submissions\/?$/i, '');
    return base;
  }

  function apiUrl(path) {
    const base = apiBase();
    const p = path.startsWith('/') ? path : `/${path}`;
    return base ? `${base}${p}` : p;
  }

  function normalizeMondo(value) {
    const n = parseFloat(value);
    if (Number.isNaN(n)) return null;
    return (Math.round(n * 2) / 2).toFixed(1);
  }

  function extractMondoFromString(value) {
    if (value == null || value === '') return null;
    const text = String(value);
    const match = text.match(/(\d{2}(?:\.5)?)/);
    return match ? normalizeMondo(match[1]) : null;
  }

  function variantMondoCandidates(variant) {
    if (!variant) return [];
    const raw = [];
    if (variant.mondo != null) raw.push(variant.mondo);
    if (variant.optionName === mondoOptionName() && variant.option != null) raw.push(variant.option);
    raw.push(variant.option1, variant.option2, variant.option3, variant.title);
    if (Array.isArray(variant.options)) raw.push(...variant.options);
    const out = [];
    raw.forEach((value) => {
      const mondo = extractMondoFromString(value);
      if (mondo && out.indexOf(mondo) === -1) out.push(mondo);
    });
    return out;
  }

  function findVariantMatch(linerId, mondoSize, options) {
    const requireAvailable = !(options && options.requireAvailable === false);
    const catalog = cfg().linerVariants && cfg().linerVariants[linerId];
    if (!catalog || !Array.isArray(catalog) || mondoSize == null) return null;
    const target = normalizeMondo(mondoSize);
    if (!target) return null;
    const hit = catalog.find((variant) => {
      if (requireAvailable && variant.available === false) return false;
      return variantMondoCandidates(variant).some((candidate) => candidate === target);
    });
    if (!hit) return null;
    return {
      id: Number(hit.id),
      available: hit.available !== false,
      mondo: target,
    };
  }

  function resolveVariantMatch(linerId, mondoSize) {
    return findVariantMatch(linerId, mondoSize, { requireAvailable: false });
  }

  function resolveVariantId(linerId, mondoSize) {
    const hit = findVariantMatch(linerId, mondoSize, { requireAvailable: true });
    return hit ? hit.id : null;
  }

  function hasLinerCatalog(linerId) {
    const catalog = cfg().linerVariants && cfg().linerVariants[linerId];
    return !!(catalog && catalog.length);
  }

  function whenRestockRocketReady(timeoutMs) {
    const timeout = timeoutMs || 4000;
    return new Promise((resolve) => {
      if (global._RestockRocket && typeof global._RestockRocket.openModal === 'function') {
        resolve(global._RestockRocket);
        return;
      }
      const started = Date.now();
      const tick = () => {
        if (global._RestockRocket && typeof global._RestockRocket.openModal === 'function') {
          resolve(global._RestockRocket);
          return;
        }
        if (Date.now() - started >= timeout) {
          resolve(null);
          return;
        }
        global.setTimeout(tick, 100);
      };
      tick();
    });
  }

  async function fetchProductJson(handle) {
    if (!handle) return null;
    const res = await fetch(`/products/${encodeURIComponent(handle)}.js`, {
      headers: { Accept: 'application/json' },
      credentials: 'same-origin',
    });
    if (!res.ok) return null;
    return res.json();
  }

  async function openBackInStockNotify(linerId, mondoSize, customer) {
    const match = resolveVariantMatch(linerId, mondoSize);
    if (!match || !match.id) {
      throw new Error(
        mondoSize != null
          ? `No "${mondoOptionName()}" variant found for Mondo ${normalizeMondo(mondoSize)}.`
          : 'Could not determine liner size.'
      );
    }

    const productMeta = (cfg().linerProducts && cfg().linerProducts[linerId]) || {};
    const variantId = String(match.id);
    const customerData = {};
    if (customer && customer.email) customerData.email = customer.email;
    const submissionToken = customer && customer.submissionToken;

    const restockRocket = await whenRestockRocketReady();
    if (restockRocket) {
      let productData = productMeta.json || null;
      if (!productData && productMeta.handle) {
        productData = await fetchProductJson(productMeta.handle);
      }
      if (productData) {
        restockRocket.openModal(productData, variantId, customerData);
        recordEngagement(submissionToken, {
          event_type: 'notify_click',
          liner_id: linerId,
          variant_id: match.id,
          mondo: match.mondo,
          method: 'restock_alerts_modal',
        });
        return { ok: true, method: 'restock_alerts_modal', variantId: match.id };
      }
    }

    const baseUrl = productMeta.url || (global.LINER_SHOP_URL && global.LINER_SHOP_URL[linerId]);
    if (!baseUrl) throw new Error('Product page URL is not configured for this liner.');
    const sep = baseUrl.indexOf('?') === -1 ? '?' : '&';
    recordEngagement(submissionToken, {
      event_type: 'notify_click',
      liner_id: linerId,
      variant_id: match.id,
      mondo: match.mondo,
      method: 'product_redirect',
    });
    global.location.href = `${baseUrl}${sep}variant=${variantId}`;
    return { ok: true, method: 'product_redirect', variantId: match.id };
  }

  function loadDraft() {
    try {
      const raw = global.localStorage.getItem(DRAFT_KEY);
      if (!raw) return null;
      const parsed = JSON.parse(raw);
      if (!parsed || typeof parsed !== 'object' || !parsed.answers) return null;
      return parsed;
    } catch (_) {
      return null;
    }
  }

  function saveDraft(answers, step) {
    try {
      global.localStorage.setItem(
        DRAFT_KEY,
        JSON.stringify({ answers, step, savedAt: Date.now() })
      );
    } catch (_) {}
  }

  function clearDraft() {
    try {
      global.localStorage.removeItem(DRAFT_KEY);
    } catch (_) {}
  }

  function notifyCartUpdated(cart, variantId) {
    global.document.dispatchEvent(
      new CustomEvent('cart:update', {
        bubbles: true,
        detail: {
          resource: cart,
          sourceId: 'fit-quiz',
          data: {
            variantId: variantId != null ? String(variantId) : undefined,
            itemCount: cart && cart.item_count != null ? cart.item_count : undefined,
            source: 'fit-quiz',
          },
        },
      })
    );
  }

  async function submitSubmission(payload) {
    if (generationToken) await startNewEngagementAttempt();
    const gen = generation;
    const res = await fetch(apiUrl('/fit-quiz/submissions'), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(payload),
    });
    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      throw new Error(err.detail || err.error || `submit_${res.status}`);
    }
    const body = await res.json();
    if (body && body.token) {
      generationToken = body.token;
      await flushEngagementQueue(body.token, gen);
    }
    return body;
  }

  async function recallSubmission(token) {
    const res = await fetch(apiUrl(`/fit-quiz/submissions/${encodeURIComponent(token)}`), {
      headers: { Accept: 'application/json' },
    });
    if (!res.ok) throw new Error(`recall_${res.status}`);
    return res.json();
  }

  function readPendingEvents() {
    try {
      const raw = global.sessionStorage.getItem(PENDING_EVENTS_KEY);
      const parsed = raw ? JSON.parse(raw) : [];
      if (!Array.isArray(parsed)) return [];
      let dirty = false;
      const normalized = parsed.map((item, index) => {
        if (item && item.id != null && item.generation != null) return item;
        dirty = true;
        return {
          ...engagementPayload(item || {}),
          id: item && item.id != null ? item.id : `legacy-${index}`,
          generation: item && item.generation != null ? item.generation : generation,
        };
      });
      if (dirty) writePendingEvents(normalized);
      return normalized;
    } catch (_) {
      return [];
    }
  }

  function writePendingEvents(events) {
    try {
      if (!events.length) global.sessionStorage.removeItem(PENDING_EVENTS_KEY);
      else global.sessionStorage.setItem(PENDING_EVENTS_KEY, JSON.stringify(events));
    } catch (_) {}
  }

  function enqueueEvent(event, gen) {
    const pending = readPendingEvents();
    const id = pending.reduce((max, item) => Math.max(max, Number(item.id) || 0), 0) + 1;
    pending.push({ ...engagementPayload(event), generation: gen, id });
    writePendingEvents(pending);
  }

  function dropEvent(id) {
    writePendingEvents(readPendingEvents().filter((item) => item.id !== id));
  }

  function engagementPayload(event) {
    return {
      event_type: event.event_type,
      liner_id: event.liner_id || null,
      liner_name: event.liner_name || null,
      variant_id: event.variant_id || null,
      mondo: event.mondo || null,
      method: event.method || null,
    };
  }

  async function postEngagement(token, event) {
    const res = await fetch(apiUrl(`/fit-quiz/submissions/${encodeURIComponent(token)}/events`), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      keepalive: true,
      body: JSON.stringify(engagementPayload(event)),
    });
    if (!res.ok) {
      console.warn('fit-quiz engagement failed', res.status);
      throw new Error(`engagement_${res.status}`);
    }
  }

  function recordEngagement(token, event) {
    if (token && generationToken && token !== generationToken) {
      return startNewEngagementAttempt().then(() => recordEngagement(token, event));
    }
    if (token) generationToken = token;
    if (!token) {
      enqueueEvent(event, generation);
      return Promise.resolve({ queued: true });
    }
    return postEngagement(token, event)
      .then(() => flushEngagementQueue(token, generation))
      .catch((err) => {
        console.warn('fit-quiz engagement failed', err);
        enqueueEvent(event, generation);
        return flushEngagementQueue(token, generation);
      });
  }

  async function flushEngagementQueue(token, forGeneration) {
    if (!token) return;
    const gen = forGeneration == null ? generation : forGeneration;
    for (;;) {
      const next = readPendingEvents().find(
        (item) => item.generation === gen && !inflightEventIds.has(item.id)
      );
      if (!next) return;
      inflightEventIds.add(next.id);
      try {
        await postEngagement(token, next);
        dropEvent(next.id);
      } catch (err) {
        console.warn('fit-quiz engagement failed', err);
        return;
      } finally {
        inflightEventIds.delete(next.id);
      }
    }
  }

  function startNewEngagementAttempt() {
    const previousToken = generationToken;
    const previousGen = generation;
    generation += 1;
    generationToken = null;
    if (previousToken) return flushEngagementQueue(previousToken, previousGen);
    return Promise.resolve();
  }

  function clearEngagementQueue() {
    writePendingEvents([]);
  }

  async function setCartAttribute(name, value) {
    if (!name || !value) return;
    await fetch('/cart/update.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      credentials: 'same-origin',
      body: JSON.stringify({ attributes: { [name]: value } }),
    }).catch(() => {});
  }

  async function addRecommendedToCart(linerId, mondoSize, submissionToken) {
    const variantId = resolveVariantId(linerId, mondoSize);
    if (!variantId) {
      const oos = resolveVariantMatch(linerId, mondoSize);
      if (oos && !oos.available) {
        throw new Error(
          `Mondo ${normalizeMondo(mondoSize)} is out of stock. Use notify when back in stock.`
        );
      }
      throw new Error(
        mondoSize != null
          ? `No "${mondoOptionName()}" variant for Mondo ${normalizeMondo(mondoSize)} on this product.`
          : 'Could not determine liner size for add to cart.'
      );
    }

    const addRes = await fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      credentials: 'same-origin',
      body: JSON.stringify({ items: [{ id: variantId, quantity: 1 }] }),
    });
    const addData = await addRes.json().catch(() => ({}));
    if (!addRes.ok) {
      throw new Error(addData.description || addData.message || 'add_failed');
    }

    if (submissionToken) await setCartAttribute('fit_quiz_ref', submissionToken);
    notifyCartUpdated(addData, variantId);
    recordEngagement(submissionToken, {
      event_type: 'add_to_cart',
      liner_id: linerId,
      variant_id: variantId,
      mondo: normalizeMondo(mondoSize),
      method: 'cart_add',
    });
    return { ok: true, variantId, cart: addData };
  }

  const api = {
    apiUrl,
    mondoOptionName,
    normalizeMondo,
    extractMondoFromString,
    resolveVariantId,
    resolveVariantMatch,
    hasLinerCatalog,
    openBackInStockNotify,
    loadDraft,
    saveDraft,
    clearDraft,
    submitSubmission,
    recallSubmission,
    addRecommendedToCart,
    setCartAttribute,
    recordEngagement,
    flushEngagementQueue,
    clearEngagementQueue,
    startNewEngagementAttempt,
  };

  applyThemeCatalogOverrides();
  global.ZipfitFitQuizBridge = api;
  global.ZipfitFitQuizShopify = api;
})(window);
